/**
 * Light copy protection for product content, plus a share button.
 *
 * Product names, descriptions and photos (product page, product cards, quick add) cannot be
 * selected, dragged out, or saved from the right-click / long-press menu. If someone copies
 * from those areas anyway, the clipboard receives the product's link instead of the text, which
 * is what a shopper usually wants to pass on. Contact details, policies and the rest of the site
 * stay copyable. This deters casual copying only: the text remains in the page for search
 * engines and screen readers, and determined copying (screenshots, page source) is unaffected.
 *
 * Buttons marked [data-mlb-share] open the device share sheet, or copy the link where
 * sharing is unavailable.
 */
const PROTECTED =
  '.product-details, .product-information__media, .product-card, .quick-add-modal__content, .mlb-collection-about';

/** @param {Node | null} node */
const protectedArea = (node) => {
  const element = node instanceof Element ? node : node?.parentElement;
  if (!element || element.closest('input, textarea, [contenteditable="true"]')) return null;
  return element.closest(PROTECTED);
};

/**
 * The link to hand out for a protected area: a product card's own link, otherwise this page.
 * @param {Element} area
 */
const linkFor = (area) => {
  const cardLink = area.closest('.product-card')?.querySelector('a[href*="/products/"]');
  const href = cardLink?.getAttribute('href');
  const url = new URL(href || document.querySelector('link[rel="canonical"]')?.getAttribute('href') || location.href, location.href);
  url.search = '';
  url.hash = '';
  return url.toString();
};

/** @param {ClipboardEvent} event */
const onCopy = (event) => {
  const selection = document.getSelection();
  const area = protectedArea(selection?.anchorNode ?? null) || protectedArea(selection?.focusNode ?? null);
  if (!area || !event.clipboardData) return;
  event.preventDefault();
  event.clipboardData.setData('text/plain', linkFor(area));
};

document.addEventListener('copy', onCopy);
document.addEventListener('cut', onCopy);

// No "Save image as…" or dragging product photos out of the page
document.addEventListener('contextmenu', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('img, picture, video, .media') && protectedArea(target)) event.preventDefault();
});

document.addEventListener('dragstart', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target && (target instanceof HTMLImageElement || target.closest('picture')) && protectedArea(target)) {
    event.preventDefault();
  }
});

// Share buttons
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-mlb-share]') : null;
  if (!(button instanceof HTMLElement)) return;
  event.preventDefault();

  const url = button.dataset.url || location.href;
  const title = button.dataset.title || document.title;

  if (navigator.share) {
    try {
      await navigator.share({ title, url });
    } catch {
      // Closing the share sheet is not an error worth reporting
    }
    return;
  }

  const label = button.querySelector('[data-mlb-share-label]');
  try {
    await navigator.clipboard.writeText(url);
    if (label) {
      const original = label.textContent;
      label.textContent = button.dataset.copied || 'Link copied';
      setTimeout(() => {
        label.textContent = original;
      }, 2000);
    }
  } catch {
    window.prompt('Copy this link', url);
  }
});
